<template>
  <div class="cd-root">

    <!-- ── Sidebar ── -->
    <div :class="['cd-sidebar', sidebarCollapsed ? 'cd-sidebar--collapsed' : '']">
      <div class="cd-sidebar__head">
        <div class="cd-logo">
          <span class="cd-logo__icon"><cortex-icon :size="18"></cortex-icon></span>
          <span class="cd-logo__text">Kortex</span>
        </div>
        <div class="cd-sidebar__head-actions">
          <button
            class="cd-icon-btn cd-icon-btn--ghost"
            :title="$t('kortex.hideConversations')"
            @click="sidebarCollapsed = true"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="cd-icon-btn" :title="$t('kortex.newChat')" @click="newConversation">
            <i class="fas fa-plus"></i>
          </button>
        </div>
      </div>

      <div class="cd-sidebar__body">
        <div v-if="conversations.length === 0" class="cd-empty">
          <i class="fas fa-comments cd-empty__icon"></i>
          <span>{{ $t('kortex.noConversations') }}</span>
        </div>
        <div
          v-for="conv in conversations"
          :key="conv.uuid"
          :class="['cd-conv', activeConversationUUID === conv.uuid ? 'cd-conv--active' : '']"
          @click="selectConversation(conv.uuid)"
        >
          <i class="fas fa-comment cd-conv__ico"></i>
          <template v-if="editingConvUUID === conv.uuid">
            <input
              ref="renameInput"
              v-model="editingTitle"
              class="cd-conv__rename-input"
              @keydown.enter.prevent="commitRename"
              @keydown.esc.prevent="cancelRename"
              @blur="commitRename"
              @click.stop
            />
          </template>
          <span v-else class="cd-conv__label" @dblclick.stop="startRename(conv)">{{ conv.title || $t('kortex.newChat') }}</span>
          <span v-if="conv.participants && conv.participants.length > 0" class="cd-conv__agent-count">
            <i class="fas fa-users"></i> {{ conv.participants.length }}
          </span>
          <button v-if="editingConvUUID !== conv.uuid" class="cd-conv__del" :title="$t('kortex.archiveConversation')" @click.stop="archiveConversation(conv)">
            <i class="fas fa-archive"></i>
          </button>
        </div>
      </div>

      <!-- sidebar foot removed; agent picker moved to input area -->
    </div>

    <!-- ── Chat ── -->
    <div class="cd-chat">
      <div class="cd-chat__head">
        <button
          v-if="sidebarCollapsed"
          class="cd-icon-btn cd-icon-btn--ghost"
          :title="$t('kortex.showConversations')"
          @click="sidebarCollapsed = false"
        >
          <i class="fas fa-bars"></i>
        </button>
        <span class="cd-chat__title">{{ activeTitle }}</span>

        <!-- Multi-agent participants -->
        <div v-if="activeParticipants.length > 0" class="cd-participants">
          <span
            v-for="p in activeParticipants"
            :key="p.agent_uuid"
            :class="['cd-participant-chip', p.mode === 'passive' ? 'cd-participant-chip--passive' : 'cd-participant-chip--active']"
            :title="(p.instructions_override || p.name) + ' \u2014 ' + (p.mode === 'passive' ? $t('kortex.modePassive') : $t('kortex.modeActive'))"
          >
            <span class="cd-participant-chip__dot"></span>
            <span class="cd-participant-chip__name">{{ p.name }}</span>
            <button class="cd-participant-chip__remove" :title="$t('remove')" @click.stop="removeParticipant(p.agent_uuid)">
              <i class="fas fa-times"></i>
            </button>
          </span>
          <button v-if="inviteAgentOptions.length > 0" class="cd-participant-add" :title="$t('kortex.inviteAgent')" @click.stop="showAgentInvite = true">
            <i class="fas fa-plus"></i>
          </button>
        </div>
        <button
          v-else-if="inviteAgentOptions.length > 0"
          class="cd-participant-add cd-participant-add--subtle"
          :title="$t('kortex.inviteAgent')"
          @click.stop="showAgentInvite = true"
        >
          <i class="fas fa-user-plus"></i>
        </button>

        <button
          v-if="!fullscreen"
          class="cd-icon-btn cd-icon-btn--ghost"
          :title="isSplitMode ? $t('kortex.exitSplitMode') : $t('kortex.enterSplitMode')"
          @click.stop="toggleSplitMode"
        >
          <i :class="isSplitMode ? 'fas fa-compress-alt' : 'fas fa-columns'"></i>
        </button>
        <button
          v-if="!fullscreen"
          class="cd-icon-btn cd-icon-btn--ghost"
          :title="$t('kortex.openFullscreen')"
          @click.stop="openFullscreen"
        >
          <i class="fas fa-expand"></i>
        </button>
        <button
          class="cd-icon-btn cd-icon-btn--ghost"
          :title="fullscreen ? $t('kortex.exitFullscreen') : ''"
          @click.stop="closeChat"
        >
          <i :class="fullscreen ? 'fas fa-arrow-left' : 'fas fa-times'"></i>
        </button>
      </div>

      <div ref="messageArea" class="cd-messages">
        <template v-if="activeConversationUUID || messages.length">
          <template v-for="(msg, idx) in messages">
            <template v-if="msg.role === 'user' || (msg.role === 'assistant' && (msg.content || msg.thinking || msg.isError || !isStreaming))">
              <message-bubble :key="msg.uuid || idx" :message="msg" :is-active="isStreaming && idx === messages.length - 1"></message-bubble>
            </template>
          </template>

          <typing-indicator v-if="(isStreaming && !streamingContent && !streamingThinking) || isOCRLoading" :using-tools="streamingUsingTools"></typing-indicator>

          <tool-approval-card v-if="pendingToolApproval"></tool-approval-card>

          <div v-if="isStreaming" class="cd-stop-bar">
            <button class="cd-stop-btn" @click="stopGeneration">
              <i class="fas fa-stop-circle"></i>
              {{ $t('kortex.stop') }}
            </button>
          </div>

          <rag-source-card
            v-if="ragSources.length > 0"
            :sources="ragSources"
          ></rag-source-card>
        </template>

        <div v-else class="cd-welcome">
          <div class="cd-welcome__orb">
            <cortex-icon :size="36"></cortex-icon>
          </div>
          <div class="cd-welcome__heading">Kortex</div>
          <div class="cd-welcome__sub">{{ $t('kortex.welcomeMessage') }}</div>
          <div v-if="pageTools.length > 0" class="cd-welcome__caps">
            <div class="cd-welcome__caps-label">{{ $t('kortex.pageCapabilities') }}</div>
            <div class="cd-welcome__caps-list">
              <div v-for="tool in (toolsExpanded ? pageTools : pageTools.slice(0, 3))" :key="tool.function.name" class="cd-welcome__cap-chip">
                <i class="fas fa-bolt"></i>
                <span>{{ tool.function.description }}</span>
              </div>
            </div>
            <button v-if="pageTools.length > 3" class="cd-welcome__caps-toggle" @click="toolsExpanded = !toolsExpanded">
              <i :class="toolsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
              {{ toolsExpanded ? $t('kortex.showLess') : `+${pageTools.length - 3} ${$t('kortex.more')}` }}
            </button>
          </div>
        </div>
      </div>

      <!-- Input -->
      <div class="cd-input-wrap">
        <!-- OCR error -->
        <div v-if="ocrError" class="cd-ocr-error">
          <i class="fas fa-exclamation-triangle"></i>
          {{ ocrError }}
          <button class="cd-ocr-error__close" @click="ocrError = null"><i class="fas fa-times"></i></button>
        </div>

        <div :class="['cd-input-box', isStreaming ? 'cd-input-box--disabled' : '']">
          <input ref="ocrInput" type="file" accept="image/*,.pdf" class="cd-file-input" @change="onOCRFileSelected" />

          <!-- Left: Agent picker -->
          <div v-if="agents.length > 0" class="cd-input-agent-panel" @click.stop>
            <button
              class="cd-agent-icon-btn"
              :class="{ 'cd-agent-icon-btn--active': agentDropdownOpen }"
              :title="selectedAgent ? selectedAgent.name : $t('kortex.defaultAgent')"
              :disabled="isStreaming"
              @click="agentDropdownOpen = !agentDropdownOpen"
            >
              <img v-if="selectedAgentUUID === 'prota'" :src="protaAvatar" class="cd-agent-icon__img" alt="Prota" />
              <img v-else-if="selectedAgentUUID === 'proka'" :src="prokaAvatar" class="cd-agent-icon__img" alt="Proka" />
              <i v-else class="fas fa-robot"></i>
              <i class="fas fa-chevron-down cd-agent-icon-btn__chevron"></i>
            </button>
            <div v-if="agentDropdownOpen" class="cd-agent-icon__menu" @click.stop>
              <div
                v-for="agent in agentsNotInRoom"
                :key="agent.uuid"
                :class="['cd-agent-option', selectedAgentUUID === agent.uuid ? 'cd-agent-option--active' : '']"
                @click="onAgentChange(agent.uuid)"
              >
                <div :class="['cd-agent-option__avatar', (agent.uuid === 'prota' || agent.uuid === 'proka') ? 'cd-agent-option__avatar--prota' : '']">
                  <img v-if="agent.uuid === 'prota'" :src="protaAvatar" class="cd-agent-option__avatar-img" alt="Prota" />
                  <img v-else-if="agent.uuid === 'proka'" :src="prokaAvatar" class="cd-agent-option__avatar-img" alt="Proka" />
                  <i v-else class="fas fa-robot"></i>
                </div>
                <div class="cd-agent-option__body">
                  <span class="cd-agent-option__name">
                    {{ agent.name }}
                    <span v-if="agent.uuid === 'prota' || agent.uuid === 'proka' || agent.uuid === 'protokol-bot'" class="cd-agent-picker__badge">{{ $t('kortex.platform') }}</span>
                  </span>
                  <span v-if="agent.description" class="cd-agent-option__desc">{{ agent.description }}</span>
                </div>
                <i v-if="selectedAgentUUID === agent.uuid" class="fas fa-check cd-agent-option__check"></i>
              </div>
            </div>
          </div>

          <!-- Right: Text input -->
          <div class="cd-input-text-panel">
            <div class="cd-attach-menu" @click.stop>
              <button
                class="cd-attach-btn cd-attach-btn--plus"
                :disabled="isStreaming"
                :title="$t('kortex.addAttachment')"
                @click="attachMenuOpen = !attachMenuOpen"
              >
                <i class="fas fa-plus"></i>
              </button>
              <div v-if="attachMenuOpen" class="cd-attach-menu__popup">
                <button
                  v-if="canOCR"
                  class="cd-attach-menu__item"
                  :disabled="isOCRLoading"
                  @click="triggerOCRUpload(); attachMenuOpen = false"
                >
                  <i :class="isOCRLoading ? 'fas fa-spinner fa-spin' : 'fas fa-eye'"></i>
                  <span>{{ $t('kortex.ocrButton') }}</span>
                </button>
              </div>
            </div>
            <textarea
              ref="inputField"
              v-model="inputText"
              :placeholder="selectedAgent ? ('Message ' + selectedAgent.name + '...') : $t('kortex.placeholder')"
              :disabled="isStreaming"
              class="cd-textarea"
              rows="1"
              @keydown.enter.exact.prevent="handleEnterKey"
              @keydown.up.prevent="mentionNavigate(-1)"
              @keydown.down.prevent="mentionNavigate(1)"
              @keydown.esc.prevent="mentionOpen = false"
              @keydown.tab.prevent="mentionSelect"
              @input="autoResize"
            ></textarea>

            <!-- @mention suggestion dropdown -->
            <div v-if="mentionOpen && mentionMatches.length > 0" class="cd-mention-dropdown">
              <div
                v-for="(agent, idx) in mentionMatches"
                :key="agent.agent_uuid"
                :class="['cd-mention-item', idx === mentionIndex ? 'cd-mention-item--active' : '']"
                @click="mentionInsert(agent)"
                @mouseenter="mentionIndex = idx"
              >
                <span class="cd-mention-item__name">@{{ agent.name }}</span>
                <span v-if="agent.mode === 'passive'" class="cd-mention-item__mode">@mention only</span>
              </div>
            </div>
            <button
              v-if="isStreaming"
              class="cd-action-btn cd-action-btn--stop"
              @click="stopGeneration"
            >
              <i class="fas fa-stop"></i>
            </button>
            <button
              v-else
              class="cd-action-btn"
              :disabled="!inputText.trim()"
              @click="send"
            >
              <i class="fas fa-arrow-up"></i>
            </button>
          </div>
        </div>
        <div class="cd-input-footer">
          <div v-if="myAccess && myAccess.credit_limit > 0" class="cd-input-credits">
            <i class="fas fa-bolt"></i>
            {{ formatCredits(myAccess.credits_used || 0) }} / {{ formatCredits(myAccess.credit_limit) }} {{ $t('kortex.credits') }}
          </div>
          <div class="cd-input-hint">Enter to send &nbsp;·&nbsp; Shift+Enter for new line</div>
        </div>
      </div>
    </div>

    <!-- Agent invite dialog -->
    <main-dialog
      :show="showAgentInvite"
      :title="$t('kortex.inviteAgent')"
      @close="showAgentInvite = false"
    >
      <div class="cd-invite-form">
        <b-form-group :label="$t('kortex.agent')">
          <b-form-select v-model="inviteAgentUUID" :options="inviteAgentOptions">
            <template #first>
              <b-form-select-option value="" disabled>{{ $t('kortex.selectAgent') }}</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group :label="$t('kortex.participationMode')">
          <b-form-radio-group v-model="inviteMode" :options="modeOptions" buttons button-variant="outline-secondary" size="sm"></b-form-radio-group>
        </b-form-group>
        <b-form-group :label="$t('kortex.instructionsForAgent')">
          <b-form-textarea
            v-model="inviteInstructions"
            :placeholder="$t('kortex.instructionsPlaceholder')"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
      </div>
      <template #footer>
        <b-button variant="secondary" @click="showAgentInvite = false">{{ $t('cancel') }}</b-button>
        <b-button variant="primary" :disabled="!inviteAgentUUID" @click="doInviteAgent">{{ $t('kortex.invite') }}</b-button>
      </template>
    </main-dialog>

    <!-- Archive confirmation dialog -->
    <main-dialog
      :show="showArchiveDialog"
      :title="$t('kortex.archiveConversation')"
      @close="showArchiveDialog = false"
    >
      <p>{{ $t('kortex.confirmArchive') }}</p>
      <template #footer>
        <b-button variant="secondary" @click="showArchiveDialog = false">{{ $t('cancel') }}</b-button>
        <b-button variant="warning" @click="doArchive">{{ $t('kortex.archiveConversation') }}</b-button>
      </template>
    </main-dialog>
  </div>
</template>

<script>
import MessageBubble from './MessageBubble'
import TypingIndicator from './TypingIndicator'
import RagSourceCard from './RagSourceCard'
import ToolApprovalCard from './ToolApprovalCard'
import { getPageToolSchemas } from '@/helpers/cortexTools'
var protaAvatar = require('@/assets/images/prota-avatar.png')
var prokaAvatar = require('@/assets/images/proka-avatar.png')

export default {
  name: 'ChatPanel',
  components: {
    MessageBubble,
    TypingIndicator,
    RagSourceCard,
    ToolApprovalCard
  },
  props: {
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      inputText: '',
      editingConvUUID: null,
      editingTitle: '',
      agentDropdownOpen: false,
      attachMenuOpen: false,
      ocrError: null,
      showAgentInvite: false,
      inviteAgentUUID: '',
      inviteInstructions: '',
      inviteMode: 'active',
      showArchiveDialog: false,
      pendingArchiveConv: null,
      mentionOpen: false,
      mentionIndex: 0,
      mentionFilter: '',
      protaAvatar: protaAvatar,
      prokaAvatar: prokaAvatar,
      pageTools: [],
      toolsExpanded: false,
      sidebarCollapsed: false,
    }
  },
  computed: {
    conversations: function () {
      return this.$store.state.cortex.conversations
    },
    activeConversationUUID: function () {
      return this.$store.state.cortex.activeConversationUUID
    },
    messages: function () {
      return this.$store.state.cortex.messages
    },
    isStreaming: function () {
      return this.$store.state.cortex.isStreaming
    },
    isOCRLoading: function () {
      return this.$store.state.cortex.isOCRLoading
    },
    canOCR: function () {
      var tier = this.$store.state.cortex.tier
      return tier === 'standard' || tier === 'pro'
    },
    streamingContent: function () {
      return this.$store.state.cortex.streamingContent
    },
    streamingThinking: function () {
      return this.$store.state.cortex.streamingThinking
    },
    streamingUsingTools: function () {
      var msgs = this.$store.state.cortex.messages
      for (var i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant') {
          return !!(msgs[i].tool_calls && msgs[i].tool_calls.length > 0 && !msgs[i].content)
        }
      }
      return false
    },
    pendingToolApproval: function () {
      return this.$store.state.cortex.pendingToolApproval
    },
    ragSources: function () {
      return this.$store.state.cortex.ragSources
    },
    agents: function () {
      return this.$store.state.cortex.agents
    },
    selectedAgentUUID: function () {
      return this.$store.state.cortex.selectedAgentUUID
    },
    isSplitMode: function () {
      return this.$store.state.cortex.isSplitMode
    },
    activeTitle: function () {
      var self = this
      if (!self.activeConversationUUID) return self.$t('kortex.newChat')
      var conv = self.conversations.find(function (c) {
        return c.uuid === self.activeConversationUUID
      })
      if (conv && conv.title) return conv.title
      return self.$t('kortex.newChat')
    },
    agentOptions: function () {
      var opts = [{ value: null, text: this.$t('kortex.defaultAgent') }]
      this.agentsNotInRoom.forEach(function (a) {
        opts.push({ value: a.uuid, text: a.name })
      })
      return opts
    },
    agentsNotInRoom: function () {
      var self = this
      var alreadyIn = (self.activeParticipants || []).map(function (p) { return p.agent_uuid })
      return self.agents.filter(function (a) { return alreadyIn.indexOf(a.uuid) === -1 })
    },
    inviteAgentOptions: function () {
      var self = this
      var alreadyIn = (self.activeParticipants || []).map(function (p) { return p.agent_uuid })
      // Exclude the main selected agent and any already-added participants
      return self.agents
        .filter(function (a) {
          return a.uuid !== self.selectedAgentUUID && alreadyIn.indexOf(a.uuid) === -1
        })
        .map(function (a) { return { value: a.uuid, text: a.name } })
    },
    modeOptions: function () {
      return [
        { value: 'active', text: this.$t('kortex.modeActive') },
        { value: 'passive', text: this.$t('kortex.modePassive') }
      ]
    },
    mentionMatches: function () {
      if (!this.mentionOpen) return []
      var filter = this.mentionFilter.toLowerCase().replace(/\s/g, '')
      var participants = this.activeParticipants
      if (!filter) return participants
      return participants.filter(function (p) {
        return p.name.toLowerCase().replace(/\s/g, '').indexOf(filter) !== -1
      })
    },
    selectedAgent: function () {
      var self = this
      if (!self.selectedAgentUUID) return null
      return self.agents.find(function (a) { return a.uuid === self.selectedAgentUUID }) || null
    },
    activeParticipants: function () {
      var self = this
      if (!self.activeConversationUUID) return []
      var conv = self.conversations.find(function (c) {
        return c.uuid === self.activeConversationUUID
      })
      return (conv && conv.participants) || []
    },
    myAccess: function () {
      return this.$store.getters['cortex/myAccess']
    },
    creditPercent: function () {
      var ua = this.myAccess
      if (!ua || !ua.credit_limit) return 0
      return Math.min(100, Math.round(((ua.credits_used || 0) / ua.credit_limit) * 100))
    },
    creditBarColor: function () {
      var p = this.creditPercent
      if (p >= 95) return '#ef4444'
      if (p >= 80) return '#f59e0b'
      return 'rgba(108, 128, 255, 0.8)'
    }
  },
  watch: {
    inputText: function (val) {
      if (!val || val.indexOf('@') === -1) {
        this.mentionOpen = false
      }
    },
    messages: function () {
      this.$nextTick(this.scrollToBottom)
    },
    isStreaming: function (val) {
      if (!val) {
        this.$nextTick(this.scrollToBottom)
      }
    },
    activeConversationUUID: function () {
      this.$nextTick(this.scrollToBottom)
    },
    pendingToolApproval: function () {
      this.$nextTick(this.scrollToBottom)
    },
    '$store.state.cortex.isOpen': function (val) {
      if (val) {
        this.$nextTick(this.scrollToBottom)
      } else {
        this.agentDropdownOpen = false
      }
    }
  },
  mounted: function () {
    document.addEventListener('click', this.closeAgentDropdown)
    document.addEventListener('click', this.closeAttachMenu)
    window.addEventListener('kortex:capabilities-changed', this._refreshCapabilities)
    this._refreshCapabilities()
    if (this.fullscreen) {
      this.$store.dispatch('cortex/openChat')
      this.$store.dispatch('cortex/loadAgents')
    }
    this.$nextTick(this.scrollToBottom)
  },
  beforeDestroy: function () {
    document.removeEventListener('click', this.closeAgentDropdown)
    document.removeEventListener('click', this.closeAttachMenu)
    window.removeEventListener('kortex:capabilities-changed', this._refreshCapabilities)
  },
  methods: {
    _refreshCapabilities: function () {
      this.pageTools = getPageToolSchemas()
    },
    send: function () {
      var text = this.inputText.trim()
      if (!text || this.isStreaming) return
      this.inputText = ''
      this.$nextTick(function () {
        var el = this.$refs.inputField
        if (el) el.style.height = 'auto'
      }.bind(this))
      this.$store.dispatch('cortex/sendMessage', text)
    },
    newConversation: function () {
      this.$store.dispatch('cortex/createConversation')
    },
    selectConversation: function (uuid) {
      this.$store.dispatch('cortex/selectConversation', uuid)
    },
    toggleSplitMode: function () {
      this.$store.dispatch('cortex/toggleSplitMode')
    },
    closeChat: function () {
      if (this.fullscreen) {
        this.$router.go(-1)
      } else {
        this.$store.dispatch('cortex/closeChat')
      }
    },
    openFullscreen: function () {
      this.$store.dispatch('cortex/closeChat')
      this.$router.push({ name: 'CortexFullscreen' })
    },
    stopGeneration: function () {
      this.$store.dispatch('cortex/stopGeneration')
    },
    archiveConversation: function (conv) {
      this.pendingArchiveConv = conv
      this.showArchiveDialog = true
    },
    doArchive: function () {
      if (!this.pendingArchiveConv) return
      this.$store.dispatch('cortex/archiveConversation', this.pendingArchiveConv.uuid)
      this.showArchiveDialog = false
      this.pendingArchiveConv = null
    },
    doInviteAgent: function () {
      if (!this.inviteAgentUUID || !this.activeConversationUUID) return
      var agent = this.agents.find(function (a) { return a.uuid === this.inviteAgentUUID }.bind(this))
      if (!agent) return
      this.$store.dispatch('cortex/addParticipant', {
        uuid: this.activeConversationUUID,
        agent_uuid: agent.uuid,
        name: agent.name,
        mode: this.inviteMode,
        instructions_override: this.inviteInstructions
      })
      this.showAgentInvite = false
      this.inviteAgentUUID = ''
      this.inviteInstructions = ''
      this.inviteMode = 'active'
    },
    removeParticipant: function (agentUUID) {
      if (!this.activeConversationUUID) return
      this.$store.dispatch('cortex/removeParticipant', {
        uuid: this.activeConversationUUID,
        agent_uuid: agentUUID
      })
    },
    startRename: function (conv) {
      this.editingConvUUID = conv.uuid
      this.editingTitle = conv.title || ''
      var self = this
      this.$nextTick(function () {
        var inputs = self.$refs.renameInput
        var input = Array.isArray(inputs) ? inputs[0] : inputs
        if (input) { input.focus(); input.select() }
      })
    },
    commitRename: function () {
      var uuid = this.editingConvUUID
      var title = this.editingTitle.trim()
      this.editingConvUUID = null
      this.editingTitle = ''
      if (!uuid || !title) return
      this.$store.dispatch('cortex/renameConversation', { uuid: uuid, title: title })
    },
    cancelRename: function () {
      this.editingConvUUID = null
      this.editingTitle = ''
    },
    onAgentChange: function (uuid) {
      this.$store.commit('cortex/SET_SELECTED_AGENT', uuid)
      this.agentDropdownOpen = false
      var convUUID = this.$store.state.cortex.activeConversationUUID
      if (convUUID) {
        this.$store.dispatch('cortex/updateConversationAgent', { uuid: convUUID, agentUUID: uuid })
      }
    },
    triggerOCRUpload: function () {
      this.$refs.ocrInput.click()
    },
    onOCRFileSelected: function (e) {
      var file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      var self = this
      var reader = new FileReader()
      reader.onload = function (ev) {
        self.$store.dispatch('cortex/injectOCRResult', {
          fileName: file.name,
          fileData: ev.target.result,
          contentType: file.type
        }).then(function () {
          self.ocrError = null
        }).catch(function (err) {
          if (err && err.message === 'ocr_tier') {
            self.ocrError = self.$t('kortex.ocrTierError')
          } else {
            self.ocrError = self.$t('kortex.ocrError')
          }
        })
      }
      reader.readAsDataURL(file)
    },
    closeAgentDropdown: function () {
      this.agentDropdownOpen = false
    },
    closeAttachMenu: function () {
      this.attachMenuOpen = false
    },
    scrollToBottom: function () {
      var el = this.$refs.messageArea
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    },
    formatCredits: function (n) {
      if (n === 0) return '0'
      if (n >= 1) return n.toFixed(2)
      if (n >= 0.01) return n.toFixed(4)
      if (n >= 0.0001) return n.toFixed(6)
      return n.toExponential(2)
    },
    handleEnterKey: function () {
      if (this.mentionOpen && this.mentionMatches.length > 0) {
        this.mentionSelect()
      } else {
        this.send()
      }
    },
    mentionNavigate: function (dir) {
      if (!this.mentionOpen) return
      var len = this.mentionMatches.length
      if (len === 0) return
      this.mentionIndex = ((this.mentionIndex + dir) % len + len) % len
    },
    mentionSelect: function () {
      if (!this.mentionOpen) return false
      var matches = this.mentionMatches
      if (matches.length === 0) return false
      var idx = Math.min(this.mentionIndex, matches.length - 1)
      this.mentionInsert(matches[idx])
      return true
    },
    mentionInsert: function (agent) {
      var el = this.$refs.inputField
      if (!el) return
      var text = this.inputText
      // Find the last @ before cursor (or end of text)
      var pos = el.selectionStart || text.length
      var before = text.slice(0, pos)
      var after = text.slice(pos)
      var atIdx = before.lastIndexOf('@')
      if (atIdx === -1) return
      // Replace @partial with @AgentName
      var newText = before.slice(0, atIdx) + '@' + agent.name + ' ' + after
      this.inputText = newText
      this.mentionOpen = false
      this.$nextTick(function () {
        if (el) {
          var newPos = atIdx + agent.name.length + 2
          el.selectionStart = newPos
          el.selectionEnd = newPos
          el.focus()
        }
      })
    },
    autoResize: function () {
      var el = this.$refs.inputField
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 160) + 'px'

      // @mention detection
      var text = this.inputText
      var pos = el.selectionStart || text.length
      var before = text.slice(0, pos)
      var atIdx = before.lastIndexOf('@')
      if (atIdx !== -1 && (atIdx === 0 || before[atIdx - 1] === ' ')) {
        // Don't trigger on email addresses or @@
        var afterAt = before.slice(atIdx + 1)
        var spaceIdx = afterAt.indexOf(' ')
        // If there's already a space after @word, the mention is complete — don't reopen
        if (spaceIdx >= 0) {
          this.mentionOpen = false
          return
        }
        var filter = afterAt
        if (filter.indexOf('@') === -1 && this.activeParticipants.length > 0) {
          this.mentionFilter = filter
          this.mentionOpen = true
          this.mentionIndex = 0
          return
        }
      }
      this.mentionOpen = false
    }
  }
}
</script>

<style scoped lang="scss">
/* ── Root ── */
.cd-root {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #1a1d27;
  color: rgba(255, 255, 255, 0.82);
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
}

/* ── Sidebar ── */
.cd-sidebar {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #13151e;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  transition: width 0.22s ease, min-width 0.22s ease, border-color 0.22s ease;
  overflow: hidden;

  &--collapsed {
    width: 0;
    min-width: 0;
    border-right-color: transparent;
  }
}

.cd-sidebar__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 14px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.cd-sidebar__head-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.cd-logo {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cd-logo__icon {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #6c80ff, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(108, 128, 255, 0.35);
}

.cd-logo__text {
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: -0.01em;
}

.cd-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 15px;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    border-color: rgba(255, 255, 255, 0.18);
  }

  &--ghost {
    border-color: transparent;
  }
}

.cd-sidebar__body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 6px;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }
}

.cd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.2);
  font-size: 12px;
  text-align: center;
}

.cd-empty__icon {
  font-size: 24px;
  opacity: 0.4;
}

.cd-conv {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &--active {
    background: rgba(108, 128, 255, 0.1);
    border-left: 2px solid rgba(108, 128, 255, 0.5);
    padding-left: 8px;

    .cd-conv__label {
      color: rgba(168, 183, 255, 0.9);
    }

    .cd-conv__ico {
      color: rgba(108, 128, 255, 0.7);
    }
  }
}

.cd-conv__ico {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.cd-conv__label {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.cd-conv__agent-count {
  font-size: 10px;
  color: rgba(108, 128, 255, 0.55);
  padding: 1px 5px;
  border-radius: 8px;
  background: rgba(108, 128, 255, 0.08);
  flex-shrink: 0;
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;

  i { font-size: 8px; }
}

.cd-conv__rename-input {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(108, 128, 255, 0.5);
  border-radius: 4px;
  padding: 1px 5px;
  outline: none;
  line-height: 1.4;
  width: 100%;
}

.cd-conv__del {
  opacity: 0.4;
  transition: opacity 0.12s, color 0.12s;
  background: none;
  border: none;
  padding: 2px 4px;
  margin-left: auto;
  cursor: pointer;
  color: rgba(255, 100, 100, 0.7);
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 20px;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
  }
}

.cd-conv:hover .cd-conv__del {
  opacity: 1;
}

.cd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.2);
  font-size: 12px;
  text-align: center;
}

.cd-empty__icon {
  font-size: 24px;
  opacity: 0.4;
}

.cd-agent-picker {
  position: relative;
  cursor: pointer;
}

.cd-agent-picker__selected {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.04);
  transition: border-color 0.15s, background 0.15s;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.14);
  }
}

.cd-agent-picker__avatar {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(108, 128, 255, 0.25), rgba(118, 75, 162, 0.25));
  border: 1px solid rgba(108, 128, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(168, 183, 255, 0.8);
  font-size: 12px;
  flex-shrink: 0;
}

.cd-agent-picker__avatar-img,
.cd-agent-option__avatar-img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
  image-rendering: auto;
}

.cd-agent-picker__avatar--prota {
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 50%;
}

.cd-agent-option__avatar--prota {
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 50%;
}

.cd-agent-picker__badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1px 6px;
  border-radius: 4px;
  background: linear-gradient(135deg, rgba(108, 128, 255, 0.2), rgba(118, 75, 162, 0.2));
  color: rgba(168, 183, 255, 0.8);
  border: 1px solid rgba(108, 128, 255, 0.15);
  margin-left: 6px;
  vertical-align: middle;
}

.cd-agent-picker__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.cd-agent-picker__name {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.cd-agent-picker__desc {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.cd-agent-picker__chevron {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
  transition: color 0.15s;

  .cd-agent-picker:hover & {
    color: rgba(255, 255, 255, 0.45);
  }
}

.cd-agent-picker__menu {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #1e2130;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 100;
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }
}

.cd-agent-option {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 8px 9px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &--active {
    background: rgba(108, 128, 255, 0.1);
  }
}

.cd-agent-option__avatar {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, rgba(108, 128, 255, 0.2), rgba(118, 75, 162, 0.2));
  border: 1px solid rgba(108, 128, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(168, 183, 255, 0.7);
  font-size: 11px;
  flex-shrink: 0;
  margin-top: 1px;

  &--default {
    background: linear-gradient(135deg, rgba(52, 211, 153, 0.15), rgba(16, 185, 129, 0.1));
    border-color: rgba(52, 211, 153, 0.2);
    color: rgba(52, 211, 153, 0.7);
  }
}

.cd-agent-option__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cd-agent-option__name {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.3;
}

.cd-agent-option__desc {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  line-height: 1.35;
  white-space: normal;
}

.cd-agent-option__check {
  font-size: 11px;
  color: rgba(108, 128, 255, 0.8);
  flex-shrink: 0;
  margin-top: 3px;
}

.cd-agent-option__divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
  margin: 4px 2px;
}

/* ── Agent icon picker (inside input box) ── */
.cd-agent-icon__img {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.cd-agent-icon__menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: 240px;
  background: #1e2130;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 200;
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }
}

/* ── Chat ── */
.cd-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background:
    radial-gradient(ellipse 60% 40% at 80% 5%, rgba(108, 128, 255, 0.06) 0%, transparent 70%),
    radial-gradient(ellipse 50% 35% at 15% 92%, rgba(118, 75, 162, 0.05) 0%, transparent 65%),
    #1a1d27;
}

.cd-chat__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 52px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.cd-chat__title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
}

/* ── Participant chips ── */
.cd-participants {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-right: 8px;
}

.cd-participant-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: default;
  transition: background 0.15s;

  &--active {
    background: rgba(108, 128, 255, 0.12);
    border: 1px solid rgba(108, 128, 255, 0.3);
    color: rgba(255, 255, 255, 0.85);
  }

  &--passive {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.45);
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;

    .cd-participant-chip--active & {
      background: #6c80ff;
      box-shadow: 0 0 0 2px rgba(108, 128, 255, 0.25);
    }

    .cd-participant-chip--passive & {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &__name {
    line-height: 1;
  }

  &__remove {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 4px;
    font-size: 9px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s, background 0.15s;
    color: inherit;

    .cd-participant-chip:hover & {
      opacity: 0.5;
    }

    &:hover {
      opacity: 1 !important;
      background: rgba(255, 255, 255, 0.12);
    }
  }
}

.cd-participant-add {
  background: none;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  color: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  padding: 4px 10px;
  font-size: 11px;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    border-color: rgba(108, 128, 255, 0.45);
    color: rgba(108, 128, 255, 0.9);
    background: rgba(108, 128, 255, 0.08);
  }

  &--subtle {
    padding: 5px 8px;
    opacity: 0.35;
    &:hover { opacity: 1; }
  }
}

/* ── Messages ── */
.cd-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 8px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }
}

.cd-tool-calls {
  margin: 0 0 8px;
}

/* ── Welcome ── */
.cd-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  text-align: center;
  padding: 40px 20px;
}

.cd-welcome__orb {
  width: 72px;
  height: 72px;
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(108,128,255,0.15), rgba(118,75,162,0.15));
  border: 1px solid rgba(108, 128, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow:
    0 0 0 8px rgba(108, 128, 255, 0.04),
    0 0 0 18px rgba(108, 128, 255, 0.02),
    0 8px 28px rgba(108, 128, 255, 0.2);

  i {
    font-size: 30px;
    background: linear-gradient(135deg, #6c80ff, #a078d0);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.cd-welcome__heading {
  font-size: 16px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: -0.02em;
  margin-bottom: 8px;
}

.cd-welcome__sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.28);
  max-width: 280px;
  line-height: 1.6;
}

.cd-welcome__caps {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  width: 100%;
  max-width: 360px;
}

.cd-welcome__caps-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.22);
}

.cd-welcome__caps-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
}

.cd-welcome__caps-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.28);
  padding: 2px 4px;
  margin-top: 2px;
  transition: color 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.55);
  }

  i {
    font-size: 9px;
  }
}

.cd-welcome__cap-chip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
  padding: 7px 10px;
  text-align: left;

  i {
    font-size: 10px;
    color: #6c80ff;
    margin-top: 2px;
    flex-shrink: 0;
  }

  span {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    line-height: 1.5;
  }
}

/* ── Input ── */
.cd-input-wrap {
  padding: 12px 16px 24px;
  flex-shrink: 0;
}

.cd-input-box {
  display: flex;
  align-items: stretch;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: rgba(108, 128, 255, 0.45);
    box-shadow: 0 0 0 3px rgba(108, 128, 255, 0.08);
  }

  &--disabled {
    opacity: 0.5;
  }
}

/* Left agent panel */
.cd-input-agent-panel {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border-right: 1px solid rgba(255, 255, 255, 0.07);
  padding: 0 10px;
  border-radius: 14px 0 0 14px;
}

/* Right text panel */
.cd-input-text-panel {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-end;
  background: rgba(255, 255, 255, 0.04);
  padding: 0 4px 0 4px;
  position: relative;
  border-radius: 0 14px 14px 0;
}

/* Agent icon button (left panel) */
.cd-agent-icon-btn {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  padding: 8px 4px;
  cursor: pointer;
  color: rgba(255,255,255,0.6);
  transition: color 0.15s;

  &:hover:not(:disabled) {
    color: rgba(255,255,255,0.9);
  }

  &--active {
    color: rgba(108,128,255,1);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  &__chevron {
    font-size: 8px;
    opacity: 0.5;
  }
}

.cd-textarea {
  display: block;
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  line-height: 1.6;
  padding: 13px 52px 13px 16px;
  resize: none;
  overflow-y: auto;
  max-height: 160px;
  border-radius: 14px;
  font-family: inherit;

  &::placeholder {
    color: rgba(255, 255, 255, 0.22);
  }

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
}

.cd-action-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #6c80ff, #764ba2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 16px;
  padding: 0;
  box-shadow: 0 3px 12px rgba(108, 128, 255, 0.4), 0 1px 0 rgba(255,255,255,0.12) inset;

  &:hover {
    transform: scale(1.06) translateY(-1px);
    box-shadow: 0 5px 18px rgba(108, 128, 255, 0.5), 0 1px 0 rgba(255,255,255,0.12) inset;
  }

  &:active {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.25;
    transform: none;
    cursor: not-allowed;
    box-shadow: none;
  }

  &--stop {
    background: linear-gradient(135deg, #e05252, #c0392b);
    box-shadow: 0 3px 12px rgba(200, 60, 60, 0.35), 0 1px 0 rgba(255,255,255,0.12) inset;

    &:hover {
      transform: scale(1.06) translateY(-1px);
      box-shadow: 0 5px 18px rgba(200, 60, 60, 0.5), 0 1px 0 rgba(255,255,255,0.12) inset;
    }
  }
}

.cd-input-footer {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}

.cd-input-credits {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 0.01em;

  i {
    font-size: 10px;
    margin-right: 2px;
    color: rgba(108, 128, 255, 0.6);
  }
}

.cd-input-hint {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.15);
  text-align: center;
  letter-spacing: 0.01em;

  &__link {
    cursor: pointer;
    color: rgba(255, 255, 255, 0.35);
    text-decoration: underline;
    &:hover { color: rgba(255, 255, 255, 0.6); }
  }
}

/* ── Credits meter ── */
.cd-credits {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.cd-credits__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.cd-credits__label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.25);
}

.cd-credits__count {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
  font-variant-numeric: tabular-nums;
}

.cd-credits__unlimited {
  font-size: 11px;
  color: rgba(52, 211, 153, 0.7);
}

.cd-credits__bar-wrap {
  height: 3px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
}

.cd-credits__bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.4s ease, background 0.3s ease;
}

/* ── Image previews ── */
.cd-file-input {
  display: none;
}

.cd-image-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px 0;
}

.cd-image-thumb {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.1);

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__remove {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0,0,0,0.7);
    border: none;
    color: rgba(255,255,255,0.8);
    font-size: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &:hover { background: rgba(200,60,60,0.8); }
  }
}

/* ── OCR error banner ── */
.cd-ocr-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 12px 0;
  padding: 8px 10px;
  background: rgba(220, 60, 60, 0.12);
  border: 1px solid rgba(220, 60, 60, 0.3);
  border-radius: 8px;
  color: rgba(255, 120, 120, 0.9);
  font-size: 12px;

  &__close {
    margin-left: auto;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0;
    font-size: 11px;
    opacity: 0.7;
    &:hover { opacity: 1; }
  }
}

.cd-ocr-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 12px 0;
  padding: 8px 10px;
  background: rgba(80, 140, 255, 0.1);
  border: 1px solid rgba(80, 140, 255, 0.25);
  border-radius: 8px;
  color: rgba(140, 180, 255, 0.9);
  font-size: 12px;

  i { animation: fa-spin 1s infinite linear; }
}

/* ── Attach menu ── */
.cd-attach-menu {
  position: relative;
  flex-shrink: 0;

  &__popup {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    background: #1e1e2e;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 180px;
    z-index: 100;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 7px;
    background: transparent;
    border: none;
    color: rgba(255,255,255,0.75);
    font-size: 13px;
    cursor: pointer;
    text-align: left;
    width: 100%;
    transition: background 0.12s, color 0.12s;

    i { width: 16px; text-align: center; font-size: 14px; }

    &:hover:not(:disabled) {
      background: rgba(255,255,255,0.07);
      color: #fff;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
}

/* ── Attach button ── */
.cd-attach-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.35);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.15s;
  padding: 0;
  margin-right: 2px;

  &:hover:not(:disabled) {
    color: rgba(255,255,255,0.7);
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }

  &--plus {
    font-size: 12px;
  }

  &--active {
    color: rgba(108, 128, 255, 0.8);
  }
}

/* ── Stop button in message area ── */
.cd-stop-bar {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
}

.cd-stop-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 8px;
  border: 1px solid rgba(220, 70, 70, 0.35);
  background: rgba(220, 70, 70, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s;

  i {
    font-size: 13px;
    color: #e05252;
  }

  &:hover {
    background: rgba(220, 70, 70, 0.2);
    border-color: rgba(220, 70, 70, 0.55);
    color: #fff;
  }

  &:active {
    transform: scale(0.97);
  }
}

.cd-mention-dropdown {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  background: #1e2130;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 200px;
  z-index: 100;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.cd-mention-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  gap: 8px;

  &--active {
    background: rgba(108, 128, 255, 0.15);
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: #fff;
  }

  &__mode {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
  }
}
</style>
